/**
 * Провайдеры Refine.
 *
 * `authProvider` — форма взята из urtDesign, но политика другая: там «авторизация»
 * это наличие Bearer-токена в localStorage, здесь — httpOnly-cookie, которую JS
 * не видит вовсе. Поэтому `check` спрашивает сервер (`/api/me`), а не хранилище:
 * узнать, жива ли сессия, можно только у того, кто её выдал.
 */

import type { AuthProvider, DataProvider } from "@refinedev/core";

import { apiFetch, ApiFailure, query } from "@/lib/api";
import { loginPath, returnPath } from "@/lib/loginReturn";

/** Кто вошёл. Приходит из CRM (`sUser`), своей таблицы пользователей нет. */
export type CurrentUser = {
  id: number;
  name: string;
  short: string;
  /** База CRM, из которой кабинет читает числа: `crm-prod` или `crm-dev`. */
  crm?: string;
  crm_is_prod?: boolean;
  /** Коды полномочий supply (`app/core/modules/auth/permission/registry.py`).
   *  Решает сервер; список нужен, чтобы экран не показывал то, что откажут. */
  permissions: string[];
};

export const authProvider: AuthProvider = {
  check: async () => {
    try {
      await apiFetch<CurrentUser>("/api/me");
      return { authenticated: true };
    } catch {
      return { authenticated: false, redirectTo: loginPath(), logout: true };
    }
  },

  // Одно поле — пароль: он же идентифицирует человека в CRM. Логина в наших
  // системах не существует, поэтому и в провайдере его нет.
  login: async ({ password }: { password: string }) => {
    try {
      await apiFetch<CurrentUser>("/api/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      return { success: true, redirectTo: returnPath() };
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;
      return {
        success: false,
        error: {
          name: failure?.code ?? "LOGIN_FAILED",
          message: failure?.message ?? "Не удалось войти",
        },
      };
    }
  },

  logout: async () => {
    // Сессию гасит сервер: без этого строка в базе осталась бы открытой, и
    // украденная cookie продолжала бы работать.
    try {
      await apiFetch<{ ok: boolean }>("/api/logout", { method: "POST" });
    } catch {
      // Даже если сервер не ответил, уводим на форму входа: держать оператора
      // в кабинете, из которого он попросил выйти, — хуже.
    }
    return { success: true, redirectTo: "/login" };
  },

  getIdentity: async () => {
    // «Никто не вошёл» — только ответ 401. Сорвавшийся запрос (сеть, прокси
    // на рестарте) — ошибка, а не пустой пользователь: на ошибке запрос
    // оставляет прежнюю личность, и экран не гаснет из-за одной осечки.
    try {
      return await apiFetch<CurrentUser>("/api/me");
    } catch (error) {
      if (error instanceof ApiFailure && error.status === 401) return null;
      throw error;
    }
  },

  onError: async (error) => {
    // 401 приходит и от истёкшей сессии, и от погашенной администратором.
    // В обоих случаях ответ один: на форму входа, без попытки «починить».
    if (error instanceof ApiFailure && error.status === 401) {
      return { logout: true, redirectTo: loginPath(), error };
    }
    return { error };
  },
};

/**
 * Строка таблицы «Планы» — ровно то, что отдаёт `/api/plans`.
 *
 * Строка это ПАРТИЯ-ДАТА, а не отдельный план: дата, QR-папка и письмо
 * фабрике живут на ней, а планы по размерам внутри — её состав.
 *
 * Данные приходят из CRM, где закупку и ведут. Браузер туда не ходит — он
 * знает только этот адрес.
 */
export type PlanRow = {
  id: number;
  plan_code: string | null;
  plan_date: string;
  date_created: string;
  status_id: number;
  status_name: string | null;
  status_is_active: boolean;
  produce_status_id: number;
  produce_status_name: string | null;
  supplier_id: number | null;
  supplier_short: string | null;
  supplier_key: string | null;
  qr_id: number | null;
  product_id: number;
  product_name: string | null;
  product_article: string | null;
  product_image: string | null;
  model_id: number | null;
  model_group_id: number | null;
  category_name: string | null;
  /** Техкарта партии — файл, приложенный к ней в CRM. Без неё фабрике нечего шить. */
  has_card: boolean;
  /** `null` — у партии нет QR-папки, а платёж в CRM висит именно на папке. */
  is_paid: boolean | null;
  /** Сумма оплаченных платежей папки, юани; `null` — папка не оплачена или её нет. */
  paid_amount: number | null;
  /** Номера строк плана партии — ВСЕ, любой статус и количество, как меню CRM. */
  plan_line_ids: number[];
  /** Номера оплат QR-папки партии (`sCrypto_bind` вида 1). Пусто — папки или оплат нет. */
  crypto_ids: number[];
  line_count: number;
  qty_planned: number;
  qty_ordered: number;
  // Деньги приезжают СТРОКОЙ, а не числом: закупочные суммы считает база в
  // DECIMAL, и превращать их в double по дороге — способ потерять копейки.
  amount: string;
  no_price_lines: number;
};

export type PlanTotals = {
  plan_count: number;
  group_count: number;
  qty_planned: number;
  qty_ordered: number;
  amount: string;
  no_price_lines: number;
  without_qr: number;
  unpaid: number;
  without_card: number;
};

type PlansPayload = {
  rows: PlanRow[];
  totals: PlanTotals;
};

/**
 * dataProvider под наш API.
 *
 * Реализованы только методы, которые экран правда использует. Заглушки на
 * весь интерфейс «на будущее» — это код, который никто не проверял, но все
 * считают рабочим.
 */
export const dataProvider: DataProvider = {
  getApiUrl: () => "/api",

  getList: async ({ resource, filters, pagination }) => {
    if (resource !== "plans")
      throw new Error(`Ресурс ${resource} не поддерживается`);

    const params: Record<string, string | number | boolean> = {};
    for (const filter of filters ?? []) {
      if (!("field" in filter)) continue;
      if (
        filter.value === undefined ||
        filter.value === null ||
        filter.value === ""
      )
        continue;
      params[filter.field] = filter.value as string | number | boolean;
    }
    // Порции крупные намеренно: экран рассчитан на прокрутку, а не на
    // листание страниц, — виртуализация держит в DOM десятки строк из тысяч.
    params["limit"] = pagination?.pageSize ?? 1000;

    const payload = await apiFetch<PlansPayload>(`/api/plans${query(params)}`);
    return { data: payload.rows as never, total: payload.totals.plan_count };
  },

  getOne: async ({ resource, id }) => {
    if (resource !== "plans")
      throw new Error(`Ресурс ${resource} не поддерживается`);
    // Ответ — `{lines, batch_comments}` с 04.11.26 (комментарии areserv к партии).
    const payload = await apiFetch<{ lines: unknown[]; batch_comments: unknown[] }>(`/api/plans/${id}/lines`);
    return { data: { id, lines: payload.lines, batch_comments: payload.batch_comments } as never };
  },

  /**
   * Произвольный GET — им ходит экран «Планы».
   *
   * Один запрос отдаёт и строки, и сводку, и курсор: считать сводку вторым
   * запросом означало бы дважды прогнать тот же отбор по базе ради цифр,
   * которые она уже посчитала.
   */
  custom: async ({ url, method }) => {
    if (method !== "get") throw new Error(`Метод ${method} не поддерживается`);
    return { data: (await apiFetch<unknown>(url)) as never };
  },

  create: async () => {
    throw new Error("Создание партий в этой версии не реализовано");
  },
  update: async () => {
    throw new Error("Правка партий в этой версии не реализована");
  },
  deleteOne: async () => {
    throw new Error("Удаление партий не предусмотрено");
  },
};
